import { useCallback, useMemo } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Container,
  type ContainerProps,
  Divider,
  Message,
  Segment,
} from "semantic-ui-react";

import { initUseQuery } from "simplydo/core/query";
import { RichText } from "simplydo/react/richtext";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import Login from "components/entry/Login";
import { PlaceholderItem, PlaceholderStack } from "components/lib/UI";

type CustomPageProps = {
  pageId?: string;
  isPreview?: boolean;
  containerProps?: ContainerProps;
};

const CustomPage = ({
  pageId: propPageId,
  isPreview = false,
  containerProps = { style: { paddingTop: 20, paddingBottom: 20 } },
}: CustomPageProps) => {
  const urlOrganisation = useAppSelector(
    (state) => state.organisations.urlOrganisation,
  );
  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();

  const { pageId: urlPageId } = useParams<{ pageId: string }>();
  const pageId = useMemo(
    () => propPageId ?? urlPageId,
    [propPageId, urlPageId],
  );

  const pageQuery = useQuery(initUseQuery(api.pages.get, [pageId]));
  const page = pageQuery.data?.page;

  const navigateToEditPage = useCallback(
    (id: string) => {
      if (!page) return;
      let path = "";
      if (page.forType === "group") {
        path = `/groups/${page.forId}/settings/pages/edit/${id}`;
      } else if (page.forType === "organisation") {
        path = `/admin/communication/pages/edit/${id}`;
      }
      if (path) navigate(path);
    },
    [navigate, page],
  );

  const canManageGroupPages = useMemo(
    () =>
      util.hasPermission(user, "group.managePages", page?.forId) ||
      util.hasPermission(
        user,
        "org.manageGroups",
        user?.ownerOrganisation?._id,
      ),
    [user, page],
  );
  const canManageOrgPages = useMemo(
    () =>
      util.hasPermission(
        user,
        "org.viewDashboard",
        user?.ownerOrganisation?._id,
      ),
    [user],
  );

  if (pageQuery.isError && !user) {
    return (
      <Container {...containerProps}>
        <Segment
          style={{
            maxWidth: 700,
            width: 700,
            alignSelf: "center",
            padding: 50,
          }}
        >
          <Message info style={{ alignSelf: "center", marginBottom: 20 }}>
            This content is restricted to registered users, please log in to
            view this page.
          </Message>
          {/* @ts-ignore */}
          <Login embedded />
        </Segment>
      </Container>
    );
  }
  if (!page) return null;
  if (!user && !urlOrganisation) return null;

  return (
    <Container {...containerProps}>
      {!pageQuery.isLoading && pageQuery.isError ?
        <Message error>
          <Message.Header>You are unable to access this page</Message.Header>
          <Message.Content>
            This page has been restricted to certain viewers only. Please
            contact an admin if you think this is in error
          </Message.Content>
        </Message>
      : <>
          {pageQuery.isLoading ?
            <PlaceholderStack gap={10} style={{ marginBottom: 20 }}>
              <PlaceholderItem height={30} />
            </PlaceholderStack>
          : <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h1 style={{ margin: 0 }}>{page?.title ?? "New page"}</h1>
              {(
                !isPreview &&
                ((page.forType === "group" && canManageGroupPages) ||
                  (page?.forType === "organisation" && canManageOrgPages))
              ) ?
                <Button
                  icon="edit"
                  size="small"
                  content="Edit page"
                  onClick={() => navigateToEditPage(page._id)}
                  style={{ marginLeft: "auto" }}
                />
              : null}
            </div>
          }

          <Divider />

          {pageQuery.isLoading ?
            <PlaceholderStack gap={10} style={{ marginBottom: 30 }}>
              <PlaceholderItem height={30} fluid />
              <PlaceholderItem height={30} fluid />
              <PlaceholderItem height={30} fluid />
              <PlaceholderItem height={30} fluid />
              <PlaceholderItem height={30} fluid />
            </PlaceholderStack>
          : <RichText
              readOnly
              value={page?.content ?? ""}
              forType="organisation"
              forId={user ? user.organisation : urlOrganisation!._id}
            />
          }
        </>
      }
    </Container>
  );
};

export default CustomPage;
